import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Line item property linking flat pack modules to a pack cart line */
const PACK_MODULE_LINE_KEY_PROP = '_for_pack_line_key';

/**
 * @typedef {object} CartLineItem
 * @property {string} key
 * @property {number} quantity
 * @property {number} [variant_id]
 * @property {Record<string, string>} [properties]
 * @property {{ parent_key?: string, parent?: { key?: string } } | null} [parent_relationship]
 */

/**
 * @typedef {object} CartAjaxResponse
 * @property {string} [errors]
 * @property {Record<string, string>} [sections]
 * @property {CartLineItem[]} [items]
 * @property {boolean} [quantityCapped]
 * @property {number} [actualQuantity]
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {AbortController | null} */
  #quantityUpdateAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#quantityUpdateAbortController?.abort();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getLineItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getLineItemRow(line);

    if (!cartItemRowToRemove) {
      // Check if this is a complement item (child) - they're not in cartItemRows
      const complementItem = this.querySelector(`[data-line="${line}"]`);
      if (complementItem?.classList.contains('cart-items__complement')) {
        // Animate just the complement item, not the whole row
        if (prefersReducedMotion()) return complementItem.remove();

        complementItem.classList.add('removing');
        onAnimationEnd(complementItem, () => complementItem.remove());
      }
      return;
    }

    // Complements render inside the parent row; only the parent <tr> exists in the table.
    const rowsToRemove = [cartItemRowToRemove];

    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#updateQuantityRequest(config).catch((error) => {
      if (error.name === 'AbortError') return;
      console.error(error);
    });
  }

  /**
   * @param {number} quantity
   * @returns {number}
   */
  #normalizeQuantity(quantity) {
    return Math.max(0, Math.floor(Number(quantity)) || 0);
  }

  /**
   * @param {number} line - 1-based Shopify cart line index.
   * @returns {HTMLTableRowElement | undefined}
   */
  #getLineItemRow(line) {
    const rowByLine = this.querySelector(`tr.cart-items__table-row[data-line="${line}"]`);
    if (rowByLine instanceof HTMLTableRowElement) return rowByLine;

    const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);
    const rowFromInput = quantityInput?.closest('tr');
    if (rowFromInput instanceof HTMLTableRowElement) return rowFromInput;

    return undefined;
  }

  /**
   * @param {number} line - 1-based Shopify cart line index.
   * @returns {HTMLInputElement | null}
   */
  #getQuantityInputForLine(line) {
    const row = this.#getLineItemRow(line);
    const rowInput = row?.querySelector('input[data-cart-line]');
    if (rowInput instanceof HTMLInputElement) return rowInput;

    const input = this.querySelector(`input[data-cart-line="${line}"]`);
    if (input instanceof HTMLInputElement) return input;

    return null;
  }

  /**
   * @param {Response} response
   * @returns {Promise<CartAjaxResponse>}
   */
  async #parseCartJsonResponse(response) {
    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`Cart request failed: HTTP ${response.status}`);
    }

    try {
      return JSON.parse(responseText);
    } catch {
      throw new Error('Cart response was not valid JSON');
    }
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  async #updateQuantityRequest(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#quantityUpdateAbortController?.abort();
    this.#quantityUpdateAbortController = new AbortController();
    const { signal } = this.#quantityUpdateAbortController;

    this.#disableCartItems();

    const { line } = config;
    const quantity = this.#normalizeQuantity(config.quantity);
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    cartTotal?.shimmer();

    try {
      const row = this.#getLineItemRow(line);
      const parentKey = row?.dataset.key;
      const hasComplements = row?.dataset.hasComplements === 'true';
      const complementKeys = row?.dataset.complementKeys?.split(',').filter(Boolean) ?? [];

      let parsedResponseText;

      if (hasComplements && parentKey) {
        const keys =
          complementKeys.length > 0
            ? complementKeys
            : this.#getLinkedComplementKeys((await this.#fetchCart(signal)).items ?? [], parentKey);

        if (signal.aborted) return;

        parsedResponseText = await this.#updateLinkedLineQuantity(
          line,
          quantity,
          parentKey,
          keys,
          sectionsToUpdate,
          signal
        );
      } else {
        parsedResponseText = await this.#postCartLineChange(line, quantity, sectionsToUpdate, signal);
      }

      if (signal.aborted) return;

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      if (parsedResponseText.quantityCapped && parsedResponseText.actualQuantity != null) {
        this.#handleQuantityCapError(line, parsedResponseText.actualQuantity);
      }

      this.#applyCartResponse(parsedResponseText);
    } catch (error) {
      if (error.name === 'AbortError') return;
      throw error;
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<{ items?: CartLineItem[] }>}
   */
  async #fetchCart(signal) {
    const response = await fetch(Theme.routes.cart_url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });

    return /** @type {Promise<{ items?: CartLineItem[] }>} */ (this.#parseCartJsonResponse(response));
  }

  /**
   * @param {CartLineItem} item
   * @returns {string | undefined}
   */
  #getParentLineKey(item) {
    return item.parent_relationship?.parent_key ?? item.parent_relationship?.parent?.key;
  }

  /**
   * @param {CartLineItem} item
   * @returns {string | undefined}
   */
  #getFlatPackLineKey(item) {
    return item.properties?.[PACK_MODULE_LINE_KEY_PROP];
  }

  /**
   * @param {CartLineItem[]} cartItems
   * @param {string} parentKey
   * @returns {string[]}
   */
  #getLinkedComplementKeys(cartItems, parentKey) {
    return (
      cartItems
        ?.filter((item) => {
          if (this.#getParentLineKey(item) === parentKey && item.key) return true;
          return this.#getFlatPackLineKey(item) === parentKey && Boolean(item.key);
        })
        .map((item) => item.key) ?? []
    );
  }

  /**
   * Updates parent quantity first, then syncs complement lines to the parent's actual quantity.
   * Works for nested complements (parent_relationship) and flat pack-linked complements (line keys in data-complement-keys).
   * @param {number} line
   * @param {number} requestedQuantity
   * @param {string} parentKey
   * @param {string[]} complementKeys
   * @param {Set<string>} sectionsToUpdate
   * @param {AbortSignal} signal
   * @returns {Promise<CartAjaxResponse>}
   */
  async #updateLinkedLineQuantity(line, requestedQuantity, parentKey, complementKeys, sectionsToUpdate, signal) {
    const parentResponse = await this.#postCartLineChange(line, requestedQuantity, sectionsToUpdate, signal);

    if (parentResponse.errors) {
      return parentResponse;
    }

    const parentItem = parentResponse.items?.find((item) => item.key === parentKey);
    const actualParentQty =
      parentItem?.quantity ?? (requestedQuantity === 0 ? 0 : requestedQuantity);

    let cartItems = parentResponse.items ?? [];

    if (
      complementKeys.length > 0 &&
      complementKeys.some((key) => !cartItems.some((item) => item.key === key))
    ) {
      const fullCart = await this.#fetchCart(signal);
      if (signal.aborted) return parentResponse;
      cartItems = fullCart.items ?? cartItems;
    }

    const complementUpdates = this.#buildComplementQuantityUpdates(
      complementKeys,
      cartItems,
      actualParentQty
    );

    let finalResponse = parentResponse;

    if (Object.keys(complementUpdates).length > 0) {
      finalResponse = await this.#postCartQuantityUpdates(complementUpdates, sectionsToUpdate, signal);
    }

    if (requestedQuantity > actualParentQty) {
      return {
        ...finalResponse,
        quantityCapped: true,
        actualQuantity: actualParentQty,
      };
    }

    return finalResponse;
  }

  /**
   * @param {string[]} complementKeys
   * @param {CartLineItem[]} cartItems
   * @param {number} targetQuantity
   * @returns {Record<string, number>}
   */
  #buildComplementQuantityUpdates(complementKeys, cartItems, targetQuantity) {
    /** @type {Record<string, number>} */
    const updates = {};

    for (const key of complementKeys) {
      const child = cartItems.find((item) => item.key === key);

      if (child && child.quantity !== targetQuantity) {
        updates[key] = targetQuantity;
      }
    }

    return updates;
  }

  /**
   * @param {Record<string, number>} updates
   * @param {Set<string>} sectionsToUpdate
   * @param {AbortSignal} signal
   */
  async #postCartQuantityUpdates(updates, sectionsToUpdate, signal) {
    const response = await fetch(
      Theme.routes.cart_update_url,
      {
        ...fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }),
        }),
        signal,
      }
    );

    return this.#parseCartJsonResponse(response);
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @param {Set<string>} sectionsToUpdate
   * @param {AbortSignal} signal
   */
  async #postCartLineChange(line, quantity, sectionsToUpdate, signal) {
    const response = await fetch(
      Theme.routes.cart_change_url,
      {
        ...fetchConfig('json', {
          body: JSON.stringify({
            line,
            quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }),
        }),
        signal,
      }
    );

    return this.#parseCartJsonResponse(response);
  }

  /**
   * @param {CartAjaxResponse} parsedResponseText
   */
  #applyCartResponse(parsedResponseText) {
    const sectionHtml = parsedResponseText.sections?.[this.sectionId];

    if (!sectionHtml) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, sectionHtml, {
      mode: this.isDrawer ? 'hydration' : 'full',
    });

    resetShimmer(this);
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {CartAjaxResponse} parsedResponseText - The parsed response text.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.#getQuantityInputForLine(line);

    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    this.#showCartItemError(line, parsedResponseText.errors ?? '');
  };

  /**
   * @param {number} line
   * @param {number} actualQuantity
   */
  #handleQuantityCapError(line, actualQuantity) {
    const quantityInput = this.#getQuantityInputForLine(line);

    if (quantityInput) {
      quantityInput.value = actualQuantity.toString();
      quantityInput.defaultValue = actualQuantity.toString();
    }

    const errorTemplate = Theme.translations?.quantity_error_max ?? '';
    const errorMessage = errorTemplate.replace('{{ maximum }}', actualQuantity.toString());

    this.#showCartItemError(line, errorMessage);
  };

  /**
   * @param {number} line
   * @param {string} message
   */
  #showCartItemError(line, message) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {CartAjaxResponse} updatedCart - The updated cart object.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      if (item.variant_id == null) continue;

      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
